import React, { useState, useEffect } from 'react';

import { Grid, InputAdornment } from '@material-ui/core';
import { getDeerPulseKickstartRoute } from 'Deer/constants/nonDeerRoutes';
import { L3_PATHS } from 'Deer/constants/routes';
import { useCreateNewSurveyForm } from 'Deer/pages/Company/Engagement/Surveys/SurveyCreation/dataHook';
import MeshGenericTable from 'components/MeshGenericTable/MeshGenericTable';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { Container, NoResultsText, StyledTextField, MeshSelect, noMarginSelectStyles, Button } from 'styles/admin';
import {
  ColoredCircularProgress,
  FlexContainer,
  StyledTooltip,
  VerticallyAlignedFlexContainer,
  StyledTypography,
} from 'styles/common';
import { checkNewNavEnabled, checkEnableCompanySupport } from 'utils/helpers/featureFlags';

import { PULSE_SURVEY_CREATE_STATUS, PULSE_KICKSTART_STEPS } from 'constants/ProjectConstants';
import { getPulseKickstartRoute } from 'constants/helper';
import { SearchIcon } from 'constants/icons';
import { KH_LINKS } from 'constants/knowledgeHubLinks';

import { getKickstartSurveyList } from 'services/engagement';
import { showErrorSnackbar } from 'services/snackbar';

import CircularOutlineIcon from 'uiToolkit/CircularOutlineIcon';
import TooltipInfo from 'uiToolkit/TooltipInfo';
import { Typography17 } from 'uiToolkit/Typography';
import { TextHeader2 } from 'uiToolkitV1/Typography';

const getDriverOptions = ({ data }) => [
  { label: 'All', value: 'ALL' },
  ...[...new Set(data.flatMap((item) => item.surveyDrivers))].map((item) => ({
    label: item,
    value: item,
  })),
];

const menuOptions = [
  {
    label: 'All',
    value: 'ALL',
  },
  {
    label: 'Active',
    value: PULSE_SURVEY_CREATE_STATUS.ACTIVE,
  },
  {
    label: 'Draft',
    value: PULSE_SURVEY_CREATE_STATUS.DRAFT,
  },
  {
    label: 'Completed',
    value: PULSE_SURVEY_CREATE_STATUS.COMPLETED,
  },
];

export const getSurveyRedirectionRoute = ({ isNew, survey, isNewNavEnabled }) => {
  const route = (formId, step) =>
    isNewNavEnabled ? getDeerPulseKickstartRoute(formId, step) : getPulseKickstartRoute(formId, step);
  if (isNew) return route('new', PULSE_KICKSTART_STEPS[0]);

  if (survey.currentStage === PULSE_SURVEY_CREATE_STATUS.COMPLETED) {
    return route(survey.id, PULSE_KICKSTART_STEPS.slice(-1)[0]);
  }
  return route(survey.id, survey.lastEnabledSection);
};

const PulseSurveySettings = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const [search, setSearch] = useState({
    text: '',
    driver: '',
    currentStage: '',
  });
  const [loading, setLoading] = useState(false);
  const [selectedStatusMenu, setSelectedStatusMenu] = useState(null);
  const [selectedDriverMenu, setSelectedDriverMenu] = useState(null);
  const [driverMenuOptions, setDriverMenuOptions] = useState(null);
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const { mutate: createNewSurveyForm } = useCreateNewSurveyForm();

  useEffect(() => {
    if (data && data.length > 0) {
      filterData(search, data);
    }
  }, [data]);

  const filterData = (searchData, data) => {
    let result = [...data];
    if (searchData.text) {
      result = result.filter((d) => d.name.toLowerCase().indexOf(searchData.text.toLowerCase()) > -1);
    }
    if (searchData.driver && searchData.driver !== 'ALL') {
      result = result.filter((d) => d.surveyDrivers.indexOf(searchData.driver) > -1);
    }
    if (searchData.currentStage && searchData.currentStage !== 'ALL') {
      result = result.filter((d) => d.currentStage.indexOf(searchData.currentStage) > -1);
    }
    setFilteredData(result);
  };

  const onStatusSelect = (option) => {
    setSelectedStatusMenu(option);
    onSearchChange({ label: 'currentStage', value: option.value });
  };

  const onSearchChange = (option) => {
    const searchData = { ...search, [option.label]: option.value };
    setSearch(searchData);
    filterData(searchData, data);
  };

  const onDriverSelect = (option) => {
    setSelectedDriverMenu(option);
    onSearchChange({ label: 'driver', value: option.value });
  };

  const handleSurveyAction = ({ isNew, survey }) => {
    const isSurveyCreationRevamp = checkEnableCompanySupport();
    if (isSurveyCreationRevamp) {
      if (isNew) {
        // create new survey and redirect
        createNewSurveyForm();
        return;
      }

      // redirect to survey creation page with survey id
      history.push(L3_PATHS.company.engagement.surveyCreation.replace(':surveyId', survey.id));
      return;
    }

    const route = getSurveyRedirectionRoute({ isNew, survey, isNewNavEnabled: checkNewNavEnabled() });
    history.push(route);
  };

  const getData = () => {
    setLoading(true);
    getKickstartSurveyList()
      .then((res) => {
        setData(res || []);
        setDriverMenuOptions(getDriverOptions({ data: res || [] }));
      })
      .catch((err) => {
        dispatch(showErrorSnackbar('Oops! Something went wrong.'));
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    getData();
  }, []);

  const getDriversAsString = ({ drivers }) => {
    let driversString = '';
    if (drivers.length <= 3) {
      drivers.forEach((item, index) => {
        driversString += item;
        if (index !== drivers.length - 1) {
          driversString += ', ';
        }
      });
    } else {
      driversString = `${drivers[0]}, ${drivers[1]}, ${drivers[2]} +${(drivers.length - 2).toString()}`;
    }
    return driversString;
  };

  const rowComponent = (survey) => {
    const { id, name, currentStage, surveyDrivers } = survey;
    return {
      id,
      comp: (
        <StyledContainerGrid
          container
          justify="space-around"
          alignItems="center"
          onClick={() => handleSurveyAction({ survey })}
        >
          <Grid item sm={5}>
            <StyledTooltip title={name}>
              <StyledTypography truncate variant="subtitle2">
                {name}
              </StyledTypography>
            </StyledTooltip>
          </Grid>
          <Grid item sm={4}>
            <StyledTypography color="textSecondary" variant="subtitle2">
              {getDriversAsString({ drivers: surveyDrivers })}
            </StyledTypography>
          </Grid>
          <Grid item sm={2}>
            <StyledTypography variant="subtitle2">
              {menuOptions.filter((menu) => menu.value === currentStage)[0]?.label}
            </StyledTypography>
          </Grid>
          <Grid item sm={1}>
            <StyledFlexContainer>
              {/* <MenuOptions /> */}
              <CircularOutlineIcon color="primary" />
            </StyledFlexContainer>
          </Grid>
        </StyledContainerGrid>
      ),
    };
  };

  const listData = () => filteredData.map((survey) => rowComponent(survey));

  const headerList = [
    {
      id: 'search',
      space: 5,
      comp: () => (
        <StyledTextField
          margin="dense"
          fullWidth
          variant="outlined"
          id="input-with-icon-textfield"
          placeholder="Search surveys"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon size="fixed16px" />
              </InputAdornment>
            ),
          }}
          style={{ width: '85%' }}
          onChange={(e) => onSearchChange({ label: 'text', value: e.target.value })}
        />
      ),
    },
    {
      id: 'driver',
      space: 4,
      comp: () => (
        <StyledSelect
          placeholder="Drivers"
          classNamePrefix="select"
          onChange={onDriverSelect}
          value={selectedDriverMenu}
          options={driverMenuOptions}
          styles={noMarginSelectStyles}
        />
      ),
    },
    {
      id: 'status',
      space: 3,
      comp: () => (
        <StyledSelect
          placeholder="Status"
          classNamePrefix="select"
          onChange={onStatusSelect}
          value={selectedStatusMenu}
          options={menuOptions}
          styles={noMarginSelectStyles}
        />
      ),
    },
  ];

  const secondaryHeaderList = [
    {
      id: 'name',
      space: 5,
      comp: () => (
        <StyledTypography variant="subtitle2" color="textSecondary" weight="bold">
          Name
        </StyledTypography>
      ),
    },
    {
      id: 'drivers',
      space: 4,
      comp: () => (
        <StyledTypography variant="subtitle2" color="textSecondary" weight="bold">
          Drivers
        </StyledTypography>
      ),
    },
    {
      id: 'status',
      space: 2,
      comp: () => (
        <StyledTypography variant="subtitle2" color="textSecondary" weight="bold">
          Status
        </StyledTypography>
      ),
    },
    {
      id: 'action',
      space: 1,
      comp: () => '',
    },
  ];

  return (
    <StyledContainer>
      <VerticallyAlignedFlexContainer style={{ justifyContent: 'space-between' }}>
        <div>
          <VerticallyAlignedFlexContainer bottomGutter={2}>
            <TextHeader2 bold>Engagement Surveys</TextHeader2>
            <TooltipInfo
              title={KH_LINKS.ENGAGEMENT_SURVEYS.description}
              linkUrl={KH_LINKS.ENGAGEMENT_SURVEYS.url}
              leftGutter={1}
            />
          </VerticallyAlignedFlexContainer>
          <Typography17>Set up engagement survey(s) for your organization</Typography17>
        </div>
        <Button blue size="large" onClick={() => handleSurveyAction({ isNew: true })} disabled={loading}>
          Add New Survey
        </Button>
      </VerticallyAlignedFlexContainer>

      {loading ? (
        <NoResultsText fullHeight>
          <ColoredCircularProgress size="tiny" />
        </NoResultsText>
      ) : (
        <>
          {filteredData && (
            <FormContainer>
              <MeshGenericTable
                success
                noDataMessage="There is no data"
                error={false}
                loading={false}
                listData={listData()}
                secondaryHeaderList={secondaryHeaderList}
                headerList={headerList}
                dataRowPadding
              />
            </FormContainer>
          )}
        </>
      )}
    </StyledContainer>
  );
};

const StyledContainer = styled(Container)`
  height: max-content;
  padding: 16px;
`;

const FormContainer = styled.div`
  margin-top: 1.5rem;
  margin-bottom: 2.5rem;
`;

const StyledSelect = styled(MeshSelect)`
  .select__control {
    min-height: 40px;
  }
  width: 85%;
`;

const StyledFlexContainer = styled(FlexContainer)`
  justify-content: flex-end;
  visibility: hidden;
`;

const StyledContainerGrid = styled(Grid)`
  &:hover ${StyledFlexContainer} {
    visibility: visible;
  }
  padding: 1rem 1.5rem;
`;

export default PulseSurveySettings;
